import {Textarea} from "@heroui/input";
import {Avatar, cn} from "@heroui/react";
import {Spinner} from "@heroui/spinner";
import {Icon} from "@iconify/react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute, useNavigate} from '@tanstack/react-router'
import {useWindowSize} from "@uidotdev/usehooks";
import React, {useEffect, useRef, useState} from "react";
import {apiAction, updateQueryData, updateQueryDataItem} from "../../../api.ts";
import img_male from "../../../assets/img_male.png";
import logo from "../../../assets/logo.png";
import Content from "../../../components/Content.tsx";
import CreateConversationModal from "../../../components/modals/CreateConversationModal.tsx";
import PButton from "../../../components/PButton.tsx";
import {ApplicationStatus, MessageRelatedType, MessageType} from "../../../constants.ts";
import {useGlobalModal} from "../../../core/GlobalModal.tsx";
import {usePusher} from "../../../core/PusherContext.tsx";
import Header from "../../../Header.tsx";
import {useJwtAuth, useJwtUser} from "../../../JWTAuthProvider.tsx";
import services from "../../../services";
import {Conversation} from "../../../types";
import queryKeys from "../../../utilities/queryKeys.ts";

export const Route = createFileRoute('/_app/messages/')({
    component:      RouteComponent,
    validateSearch: (s) => {
        return {
            to: s.to as string,
        }
    }
})

function Chat({openChat, conversation, isSelected}: {
    conversation: Conversation
    openChat: any
    isSelected: boolean
}) {
    return <div className={cn("flex gap-2 p-2 items-center cursor-pointer",
        isSelected && "text-white dark:text-black bg-primary!",
        "hover:bg-secondary dark:hover:bg-neutral-900"
    )}
                onClick={() => openChat(conversation.uid)}
    >
        <ChatTitle showLastMessage={true} conversation={conversation} />
    </div>
}

function ChatTitle({conversation, showLastMessage = false}: {
    conversation: Conversation | any,
    showLastMessage?: boolean
}) {
    const avatar = conversation.uid === 'system'
        ? logo
        : conversation._friend.profile_thumb_url || conversation._friend.profile_thumb_url || img_male;

    return <>
        <div>
            <Avatar className={""} size={"sm"} src={avatar} />
        </div>
        <div>
            {conversation.uid === 'system' && 'ScorePal'}
            {conversation.uid !== 'system' && <>
                <div>{conversation?._friend?.club?.name || conversation?._friend?.community?.name || conversation?._friend?.name}</div>
                {showLastMessage && <em className={"text-sm"}>{conversation?.last_message?.content}</em>}
            </>}
        </div>
    </>
}

function Message({message, isFriend = true}) {
    const navigate = useNavigate();
    const user = useJwtUser();
    const queryClient = useQueryClient();

    const updateApplication = (appl, status) => {
        apiAction('applications-update', {
            uid:    appl.uid,
            values: {
                version: (appl.version || 0) + 1,
                status:  status,
            },
        }).then(() => {
            queryClient.invalidateQueries({queryKey: queryKeys.messages()});
        });
    }

    return <div className={cn("p-2 rounded-xl",
        isFriend ? "self-end bg-primary-200 dark:bg-green-800" : "self-start bg-gray-200 dark:bg-neutral-900",
    )}
    >
        <div className={"flex flex-col"}>
            <div>
                {message.content}
            </div>

            <div>

                {!!message.related_uid && <span className="float-right">
                                    {(message.message_type === MessageType.Invitation || message.message_type === MessageType.InvitationRejected) && <>
                                        {message.related_type === MessageRelatedType.Game && <PButton onPress={() => navigate({to: `/profile/games/${message.related_uid}`})}>
                                            View Game
                                        </PButton>}
                                        {message.related_type === MessageRelatedType.League && <PButton onPress={() => navigate({to: `/leagues/${message.related_uid}/home`})}>
                                            View League
                                        </PButton>}
                                        {message.related_type === MessageRelatedType.Tournament && <PButton onPress={() => navigate({to: `/tournaments/${message.related_uid}/home`})}>
                                            View Tournament
                                        </PButton>}
                                        {message.related_type === MessageRelatedType.Booking && <PButton onPress={() => navigate({to: `/courts-booking/`})}>
                                            View Bookings
                                        </PButton>}
                                    </>}
                    {(message.message_type === MessageType.BookingCreated) && <>
                        {message.related_type === MessageRelatedType.Booking && <PButton onPress={() => navigate({to: `/clubs/${user?.club?.uid}/bookings/`})}>
                            Manage Bookings
                        </PButton>}
                    </>}
                    {(message.message_type === MessageType.BookingApproved) && <>
                        {message.related_type === MessageRelatedType.Booking && <PButton onPress={() => navigate({to: `/courts-booking/`})}>
                            View Bookings
                        </PButton>}
                    </>}
                    {(message.message_type === MessageType.BookingPlayerCancelled) && <>
                        {message.related_type === MessageRelatedType.Booking && <PButton onPress={() => navigate({to: `/clubs/${user?.club?.uid}/bookings/`})}>
                            View Bookings
                        </PButton>}
                    </>}
                    {(message.message_type === MessageType.BookingOrganizerCancelled) && <>
                        {message.related_type === MessageRelatedType.Booking && <PButton onPress={() => navigate({to: `/courts-booking/`})}>
                            View Bookings
                        </PButton>}
                    </>}
                    {(message.message_type === MessageType.AssessmentCompleted) && <>
                        {message.related_type === MessageRelatedType.Assessment && <PButton color={"primary"} onPress={() => navigate({to: `/abilities/${message.related_uid}/`})}>
                            View Assessment
                        </PButton>}
                    </>}
                                </span>}

                {message.message_type === MessageType.Invitation && <>
                    {
                        (() => {
                            switch (message.application?.status) {
                                case ApplicationStatus.Invited:
                                    return <>
                                        <PButton className={"mr-2 mb-2"} color={"primary"}
                                                 onPress={() => updateApplication(message.application, ApplicationStatus.Approved)}>Accept invitation</PButton>
                                        <PButton onPress={() => updateApplication(message.application, ApplicationStatus.Rejected)}>Reject</PButton>
                                    </>
                                case ApplicationStatus.Approved:
                                    return <>
                                        <i>Invitation was accepted</i>
                                    </>
                                case ApplicationStatus.Rejected:
                                    return <>
                                        <i>Invitation was rejected</i>
                                    </>
                                default:
                                    return <>
                                        <i>Invitation has expired</i>
                                    </>
                            }
                        })()
                    }
                </>}
            </div>
        </div>
    </div>
}

function RouteComponent() {
    const navigate = useNavigate();
    const jwt = useJwtAuth();
    const search = Route.useSearch();
    const user = useJwtUser();
    const globalModal = useGlobalModal();
    const queryClient = useQueryClient();
    const pusher = usePusher();
    const [message, setMessage] = useState("");

    const messagesEndRef = useRef<HTMLDivElement>(null);

    function openChat(uid: string) {
        navigate({to: '/messages', search: {uid: uid}});
    }

    const pusherHandle = (event: any) => {
        console.debug("pusherHandle event", event)
        if (event.event === "ModelCreated" && event.table === 'messages') {
            queryClient.setQueryData(queryKeys.conversations(event.data.conversation_uid), (data: any) => {
                return {...data, messages: [...(data?.messages || []), event.data]};
            });
            refreshConversation(event.data);
        }
    };

    useEffect(() => {
        pusher.on(pusherHandle);
        return () => {
            pusher.off(pusherHandle);
        }
    }, []);

    const conversationsQuery = useQuery({
        queryKey: queryKeys.conversations(),
        queryFn:  async () => services.conversations.index(),
        enabled:  user !== undefined,
    })
    const conversationQuery = useQuery({
        queryKey: queryKeys.conversations(search.uid),
        queryFn:  async () => services.conversations.show(search.uid),
        enabled:  user !== undefined && !!search.uid,
    })

    const sendMessage = useMutation({
        mutationFn: async (message: string) => services.messages.store({conversation_uid: search.uid, content: message}),
        onMutate:   () => {
            setMessage(""); // clears the textarea
            updateQueryDataItem(queryClient, queryKeys.conversations(search.uid), {
                messages: [...(conversationQuery.data?.messages || []), {
                    uid:              +new Date(),
                    sender:           user,
                    content:          message,
                    created_at:       new Date().toISOString(),
                    conversation_uid: search.uid
                }],
            })
        },
        onSuccess:  (data) => {
            refreshConversation(data);
        },
    })

    function refreshConversation(data) {
        queryClient.invalidateQueries({queryKey: queryKeys.conversations(search.uid)})
        updateQueryData(queryClient, queryKeys.conversations(), search.uid, {last_message_uid: data.uid, last_message: data})
    }

    const conversationUid = search.uid;

    const size = useWindowSize();
    const isSm = (!!size.width && size.width < 640);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({behavior: "instant"});
    }, [conversationQuery.data]);

    useEffect(() => {
        if (user?.unread_messages_count) {
            jwt.reloadUser();
        }
    }, [conversationQuery.data]);

    return <>
        <Header showBackUrl={false} />

        <Content className={"grid grid-cols-4"} fullHeight={true}>

            {(!isSm || !search.uid) &&
                <div className={"flex flex-col rounded-tl-xl rounded-bl-xl h-full bg-white dark:bg-[#252525] border-r overflow-hidden  sm:rounded-xl sm:col-span-4"}>
                    <div className={"flex justify-end p-2"}>

                        <PButton color="primary" className={"sm:min-w-0"} onPress={() => globalModal.open(<CreateConversationModal />)}
                                 startContent={<Icon icon={"material-symbols-light:chat-add-on-outline"} className={"text-2xl light:text-secondary"} />}>
                            <span className={"sm:hidden"}>New message</span>
                        </PButton>
                    </div>

                    <Chat openChat={openChat} isSelected={search.uid === 'system'} conversation={{uid: 'system'} as any}></Chat>

                    <div className={"overflow-auto"}>
                        {conversationsQuery.data?.map(conversation =>
                            <Chat openChat={openChat} isSelected={search.uid === conversation.uid} conversation={conversation}></Chat>
                        )}
                    </div>
                </div>
            }
            {(!isSm || search.uid) &&
                <div className={"col-span-3 overflow-hidden flex flex-col bg-white dark:bg-[#252525] rounded-tr-xl rounded-br-xl h-full sm:rounded-xl sm:col-span-4"}>
                    {isSm && <div className={"p-2 border-b flex justify-between items-center"}>
                        <div className={"font-bold px-2 flex gap-2 items-center"}>
                            {!!conversationQuery.data?.conversation && <ChatTitle conversation={search.uid === 'system' ? {uid: 'system'} : conversationQuery.data.conversation}></ChatTitle>}
                        </div>
                        <PButton onPress={() => navigate({to: "/messages"})}>Back</PButton>
                    </div>}
                    <div className={"flex-1 overflow-auto bg-white dark:bg-[#252525] rounded-xl m-2 p-2 relative"}>
                        {conversationQuery.isFetching && <Spinner size={"sm"} className={"absolute right-2 top-2"} />}
                        {conversationQuery.isEnabled && conversationQuery.data?.messages?.length === 0 && <div className={"flex justify-center items-center h-full"}>
                            No messages found.
                        </div>}
                        <div className={"flex flex-col gap-2"}>
                            {(conversationQuery.isEnabled && conversationQuery.data?.messages?.map(message => {
                                return <Message message={message} isFriend={message.sender?.uid !== user?.uid}></Message>
                            }))}
                            <div ref={messagesEndRef} />
                        </div>
                    </div>
                    {conversationUid !== 'system' && <div className={"flex items-center pr-2"}>
                        <Textarea className={"p-2"} label={""}
                                  variant="flat"
                                  classNames={{label: "text-foreground dark:text-white!", inputWrapper: "dark:bg-[#2A2A2A]!", input: "py-2"}}
                                  value={message}
                                  onValueChange={setMessage}
                        ></Textarea>
                        <PButton color="primary" onPress={() => sendMessage.mutate(message)} isDisabled={!message}>Send</PButton>
                    </div>}
                </div>
            }
        </Content>
    </>
}
